import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Message, Modal } from '@arco-design/web-react';

import { useImageSelectModalListState } from '../hooks/useImageSelectModalListState';
import type { ArtifactType } from '../service';
import type { CommonImageItemType, ImageQuery } from '../types';
import { resolveImageSelectConfirmUrl } from '../utils/resolveImageSelectUrl';
import { ImagesListContent } from './listContent';

export interface ImageSelectModalProps {
  visible: boolean;
  title?: string;
  queryObj?: ImageQuery;
  /** common：通用镜像；container：容器镜像（含制品类型筛选） */
  variant?: 'common' | 'container';
  artifactType?: ArtifactType;
  lockArtifactType?: boolean;
  /** 打开弹窗时的初始选中 id */
  initialSelectedKey?: string;
  onCancel: () => void;
  onConfirm: (key: string, item: CommonImageItemType, imageUrl?: string) => void | Promise<void>;
}

/** 弹窗内选择镜像：确认后再由外层写入表单 */
export function ImageSelectModal({
  visible,
  title,
  queryObj = {},
  variant = 'common',
  artifactType,
  lockArtifactType,
  initialSelectedKey,
  onCancel,
  onConfirm,
}: ImageSelectModalProps) {
  const { t } = useTranslation();
  const listState = useImageSelectModalListState(variant, queryObj, { artifactType, lockArtifactType, enabled: visible });
  const [pendingKey, setPendingKey] = useState<string | undefined>();
  const [pendingItem, setPendingItem] = useState<CommonImageItemType | undefined>();

  useEffect(() => {
    if (!visible) return;
    setPendingKey(initialSelectedKey);
    setPendingItem(undefined);
  }, [initialSelectedKey, visible]);

  const handleSelect = useCallback((key: string, item: CommonImageItemType) => {
    setPendingKey(key);
    setPendingItem(item);
  }, []);

  const handleOk = useCallback(async () => {
    if (!pendingKey) {
      Message.error(t('common:validate.required.select', { name: t('common:image') }));
      return;
    }
    const item = pendingItem ?? (listState.data.find((row) => row.key === pendingKey) as CommonImageItemType | undefined);
    if (!item) {
      Message.error(t('common:validate.required.select', { name: t('common:image') }));
      return;
    }
    const imageUrl = await resolveImageSelectConfirmUrl(variant, item);
    await onConfirm(pendingKey, item, imageUrl);
  }, [listState.data, onConfirm, pendingItem, pendingKey, t, variant]);

  return (
    <Modal
      title={title ?? t('common:image.select')}
      visible={visible}
      style={{ width: '90vw', minWidth: 960, maxWidth: 1200 }}
      onCancel={onCancel}
      onOk={handleOk}
      unmountOnExit
      focusLock={false}
    >
      <ImagesListContent
        listState={listState}
        selectedKey={pendingKey}
        onSelect={handleSelect}
        showPagination
        hideArtifactTypeSelector={lockArtifactType}
        hideLabel
        style={{ height: 'calc(80vh - 250px)' }}
      />
    </Modal>
  );
}
